import { Component, OnInit, Input } from '@angular/core';
import { Notifier, OfflineReportPreviewService } from '../../../../../_services/offline-report-preview.service';
import { LoginUserOfflineService } from '../../../../../_services/offline/login-user-offline.service';
import { ReportPreviewContentService } from '../../../../../_services/report-preview-content.service';
import { ReportDataPlate } from '../../../../models/report-preview/report-data-plate';
import { TemplateDataPlateImageInfo, TemplateDataPlateText } from '../../../../models/report-preview/report-template';
import { ReportComponentBase, ImageComponent } from '../../../../models/report-preview/TableData';
import { TextComponent } from '../../../../models/report-preview/report-text';

@Component({
    selector: 'app-report-dataplate-container-offline',
    templateUrl: './report-dataplate.component.html',
    styleUrls: ['./report-dataplate.component.scss'],
    standalone: false
})
export class ReportDataPlateOfflineComponent implements OnInit {

  @Input() pageKey: string;
  @Input() dataPlateKey: string;
  @Input() public elementLocation: string;
  @Input() public images: ImageComponent[];
  @Input() public isFirst = false;

  reportDataPlate: ReportDataPlate;
  title: TemplateDataPlateText;

  imageInfo: TemplateDataPlateImageInfo;
  sizeUnit: string;
  private notifier: Notifier;
  readonly mirrorHorizontalAndRotate270 = 5;

  readonly dataPlateStyle = 'position: absolute; display: table;';
  readonly textInlineStyle = ' display: table-cell; word-break: normal; white-space: pre-wrap; overflow: hidden;';

  constructor(
    public designService: OfflineReportPreviewService,
    private reportPreviewContentService: ReportPreviewContentService,
    private loginUserService: LoginUserOfflineService
  ) {
  }

  ngOnInit() {
    this.notifier = {
      componentName: 'report-dataplate.component',
      ids: { pageId: this.pageKey, elementId: this.dataPlateKey }
    };
    this.reportDataPlate = this.designService.getDataPlate(this.dataPlateKey);
    this.imageInfo = this.reportDataPlate.imageInfo;
    this.title = this.imageInfo.title;
    this.sizeUnit = this.reportDataPlate.getSizeUnit() ?? 'mm';
  }

  getTitleContext(text: TemplateDataPlateText, defaultText: string = null) {
    const currentUser = this.reportPreviewContentService.userSchemas.find(u => u.id === this.loginUserService.loginUser.id);
    const pageNum = this.designService.getPageNum(this.pageKey);
    const pageTotalNum = this.designService.getPageTotalNum();
    defaultText = defaultText ?? text.text;
    switch (text.type) {
      case 'dynamic':
        const dynamicId = text.dynamicId;
        return this.reportPreviewContentService.getContent(dynamicId, defaultText, null,
          this.reportPreviewContentService.reportSchema, this.reportPreviewContentService.inspectionSchema, currentUser,
          this.reportPreviewContentService.userSchemas, this.reportPreviewContentService.tenantSchema.name, pageNum, pageTotalNum);
      case 'dynamic-editable':
        const component = new TextComponent(text.dynamicId, text.text, text.editableId, text.inputType, text.radioType, text.multiSelectDelimiter);
        return this.reportPreviewContentService.getEditableContent(component, null,
          this.reportPreviewContentService.reportSchema, this.reportPreviewContentService.inspectionSchema,
          currentUser, this.reportPreviewContentService.userSchemas, this.reportPreviewContentService.tenantSchema.name,
          pageNum, pageTotalNum);
      case 'static':
      default :
        return text.text;
    }
  }

  getComponentBase(index: number) {
    const div = new ReportComponentBase();
    div.width = this.imageInfo.width / 2;
    div.height = this.imageInfo.height / 2;
    div.x = index % 2 === 1 ? this.imageInfo.width / 2 : 0;
    div.y = index >= 2 ? this.imageInfo.height / 2 : 0;
    return div;
  }

  getImageY(image: ImageComponent) {
    return this.title.position === 'top'
      ? this.title.height
      : image.y;
  }

  getBaseComponentStyle() {
    const div = new ImageComponent(null, null, null, null, null, null);
    div.width = this.imageInfo.width;
    div.height = this.imageInfo.height;
    div.x = this.imageInfo.x;
    div.y = this.imageInfo.y;

    let style = {};
    style = this.getPositionStyle(div.x, div.y, this.sizeUnit, true);
    style = this.setBaseStyle(style, div, 1);

    return style;
  }

  getImageComponentStyle(image: ImageComponent, sizeUnit: string, index: number, isAbsolute = true, isHorizontal = true) {
    const div = this.getComponentBase(index);
    div.width = this.getImageWidth(image);
    div.height = this.getImageHeight(image);
    div.x = this.imageInfo.image.x;
    div.y = this.imageInfo.image.y;

    let style = {};
    if (isHorizontal) {
      style = this.getPositionStyle(div.x, div.y, sizeUnit, isAbsolute);
      style = this.setBaseStyle(style, div, image.orientation);
    } else {
      if (isAbsolute) {
        style['position'] = 'absolute';
      }
      style['left'] = `${div.x}${sizeUnit}`;
      style['top'] = `${div.y + div.height}${sizeUnit}`;
      style = this.setBaseStyle(style, div, image.orientation);
      style = this.setTransformStyle(style, div.x, this.sizeUnit);

      // 左上を支点として回転をかける
      style['transform-origin'] = 'left top';
      style['-webkit-transform-origin'] = 'left top';
    }

    return style;
  }

  getDivComponentStyle(index: number, sizeUnit: string, isAbsolute = true, isHorizontal = true) {
    const div = this.getComponentBase(index);

    let style = {};
    if (isHorizontal) {
      style = this.getPositionStyle(div.x, div.y, sizeUnit, isAbsolute);
      style = this.setBaseStyle(style, div, 1);
    } else {
      if (isAbsolute) {
        style['position'] = 'absolute';
      }
      style['left'] = `${div.x}${sizeUnit}`;
      style['top'] = `${div.y + div.height}${sizeUnit}`;
      style = this.setBaseStyle(style, div, 1);
      style = this.setTransformStyle(style, div.x, this.sizeUnit);

      // 左上を支点として回転をかける
      style['transform-origin'] = 'left top';
      style['-webkit-transform-origin'] = 'left top';
    }

    return style;
  }

  getTitleComponentStyle(sizeUnit: string, text: TemplateDataPlateText, isAbsolute = true, isHorizontal = true) {
    const div = new ImageComponent(null, null, null, null, null, null);
    div.width = this.imageInfo.width / 2;
    div.height = this.title.height;
    div.x = this.title.x;
    div.y = this.title.y;

    let style = {};
    if (isHorizontal) {
      style = this.getPositionStyle(div.x, div.y, sizeUnit, isAbsolute);
      style = this.setBaseStyle(style, div, 1);
    } else {
      if (isAbsolute) {
        style['position'] = 'absolute';
      }
      style['left'] = `${div.x}${sizeUnit}`;
      style['top'] = `${div.y + div.height}${sizeUnit}`;
      style = this.setBaseStyle(style, div, 1);
      style = this.setTransformStyle(style, div.x, this.sizeUnit);

      // 左上を支点として回転をかける
      style['transform-origin'] = 'left top';
      style['-webkit-transform-origin'] = 'left top';
    }

    style['z-index'] = text.zIndex;

    return style;
  }

  getPositionStyle(x: number, y: number, sizeUnit: string, isAbsolute: boolean) {
    const style = {};
    if (isAbsolute) {
      style['position'] = 'absolute';
    }
    style['left'] = `${x}${sizeUnit}`;
    style['top'] = `${y}${sizeUnit}`;

    return style;
  }

  getTextAreaTableCellStyle(text: TemplateDataPlateText) {
    const style = {};
    style['display'] = 'table-cell';
    style['font-size'] = text.fontSize !== null ? text.fontSize + this.sizeUnit : this.designService.getTemplateFontSize() + this.sizeUnit;
    style['font-family'] = text.fontFamily ?? this.designService.getTemplateFontFamily();
    style['color'] = text.fontColor;
    style['font-style'] = text.fontStyle;
    style['font-weight'] = text.fontWeight;
    style['line-height'] = text.lineHeight;
    style['background-color'] = text.backgroundColor;
    style['border-style'] = text.borderStyle;
    style['border-width'] = text.borderWidth + this.sizeUnit;
    style['border-color'] = text.borderColor;
    style['text-align'] = text.horizontalAlign;
    style['vertical-align'] = text.verticalAlign;

    return style;
  }

  getImageSize(originalWidth: number, originalHeight: number,  maxWidth: number, maxHeight: number, isOutputWidth: boolean,
    isExtend = true) {
    // not extend until maxSize if false
    if (!isExtend && originalWidth < maxWidth && originalHeight < maxHeight) {
      return isOutputWidth
        ? originalWidth
        : originalHeight;
    }

    let resultWidth = originalWidth;
    let resultHeight = originalHeight;
    while (resultWidth > maxWidth || resultHeight > maxHeight) {
      const ratio = this.getRatio(resultWidth, resultHeight, maxWidth, maxHeight);
      resultWidth *= ratio;
      resultHeight *= ratio;
    }

    // Return thumbnail size.
    return isOutputWidth
      ? resultWidth
      : resultHeight;
  }

  getRatio(width: number, height: number, maxWidth: number, maxHeight: number) {
    if (width > maxWidth && height <= maxHeight) {
      return maxWidth / width;
    } else if (width <= maxWidth && height > maxHeight) {
      return maxHeight / height;
    } else {
      if (maxWidth > maxHeight) {
        return maxHeight / height;
      } else {
        return maxWidth / width;
      }
    }
  }

  getImageWidth(image: ImageComponent) {
    return image.usedImageMode === 0 ? image.width : image.modifiedWidth;
  }

  getImageHeight(image: ImageComponent) {
    return image.usedImageMode === 0 ? image.height : image.modifiedHeight;
  }

  getImageMaxWidth() {
    return this.imageInfo.width / 2;
  }

  getImageMaxHeight() {
    const titleHeight = (this.title.position === 'top' ? this.title.height : 0)
      + (this.title.position === 'bottom' ? this.title.height : 0);
    const height = this.imageInfo.height / 2;
    return height - titleHeight;
  }

  replaceNumForStr(str: string) {
    if (!str) {
      return 0;
    }

    return Number(str.replace(/[^0-9]/g, ''));
  }

  setBaseStyle(style: any, divComponent: ReportComponentBase, orientation: number) {
    style[`width.${this.sizeUnit}`] = divComponent.width;
    style[`height.${this.sizeUnit}`] = divComponent.height;
    style['max-width'] = 'initial';
    style['max-height'] = 'initial';
    style[`margin-top.${this.sizeUnit}`] = this.getImageMarginTB(divComponent.height, orientation);
    style[`margin-bottom.${this.sizeUnit}`] = this.getImageMarginTB(divComponent.height, orientation);
    style[`margin-left.${this.sizeUnit}`] = this.getImageMarginLR(divComponent.width, orientation);
    style[`margin-right.${this.sizeUnit}`] = this.getImageMarginLR(divComponent.width, orientation);
    return style;
  }

  setTransformStyle(style: any, x: number, sizeUnit: string) {
    const properties = ['transform', '-moz-transform', '-webkit-transform', '-o-transform', '-ms-transform'];
    properties.forEach(prop => {
      style[prop] = `translateX(${x}${sizeUnit}) rotate(-90deg)`;
    });
    return style;
  }

  getImageMarginLR(width: number, orientation: number) {
    const componentWidth = width;
    const maxWidth = 0;
    if (orientation >= this.mirrorHorizontalAndRotate270) {
      const h = maxWidth - componentWidth;
      return h / 2;
    } else if (componentWidth < maxWidth) {
      const w = maxWidth - componentWidth;
      return w / 2;
    } else {
      return 0;
    }
  }

  getImageMarginTB(height: number, orientation: number) {
    const componentHeight = height;
    const maxHeight = 0;
    if (orientation >= this.mirrorHorizontalAndRotate270) {
      const w = maxHeight - componentHeight;
      return w / 2;
    } else if (componentHeight < maxHeight) {
      const h = maxHeight - componentHeight;
      return h / 2;
    } else {
      return 0;
    }
  }

  getTextAreaTableStyle(title: TemplateDataPlateText) {
    const temp = new ReportComponentBase();
    temp.x = title.y;
    temp.y = title.y;
    temp.width = this.imageInfo.width / 2;
    temp.height = title.height;
    temp.maxWidth = 0;
    temp.maxHeight = 0;
    return temp.getTextAreaTableStyle(title.isHorizontal, this.sizeUnit);
  }
}
